import { useEffect, useState } from "react";
import { LayoutGrid, Rows3, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { useAgentApplicationsCount } from "./useAgentApplicationsCount.js";
import { ApplicantOrgRow, ApplicantGridRow } from "./ApplicantCard.jsx";
import Pagination from "../components/Pagination.jsx";

import PageSkeleton from "../components/Skeleton.jsx";
// Practical default for high-volume operation (an Agent's real roster can
// run into the thousands/100k) - within the real P093 backend's own
// MaxPageSize=100 cap (WorkerApplicationService.cs), never above it.
const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 350;

function describeRosterError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  return "ไม่สามารถโหลดรายการแรงงานได้ กรุณาลองใหม่อีกครั้ง";
}

/**
 * Prompt 139D, polished for high-volume enterprise use by Prompt 139D-R2:
 * the Org Chart's "แรงงานในสายงาน Agent นี้" panel - a page/search-driven
 * roster (compact Card or dense table/list view) over the same real
 * GET /api/backoffice/worker-applications endpoint every other Agent Portal
 * applicant surface already calls, exercising the P093 contract's own real
 * `page`/`search` params (see useAgentApplicationsCount.js's own Prompt 139D
 * note - `search` is a genuine server-side SQL LIKE across name/phone/
 * nationality/agent code, WorkerApplicationRepository.cs's own ListAsync,
 * not a client-side filter). This one list already spans the full applicant
 * lifecycle (QuickRegistered through WorkerProfileCreated - see
 * applicantShared.js's EKYC_STATUS_META), so a single roster genuinely shows
 * both in-progress applicants and completed Worker Profiles without merging
 * two different backend record types or inventing client-side pagination
 * across them.
 *
 * Never renders more than one page's worth of real rows (`items`, capped at
 * PAGE_SIZE by the backend) regardless of how large `totalCount` is - a
 * roster of 100,000+ stays exactly PAGE_SIZE DOM rows, Previous/Next moves
 * the real `page` param instead of fetching or rendering everything at once.
 *
 * Prompt 139D-R2: the view toggle is icon-only (LayoutGrid/Rows3 from
 * lucide-react, per the redesign spec's explicit icon mapping) with
 * title/aria-label tooltips instead of "Card"/"Grid" text - the button's
 * accessible name and its rendered text must never be the same literal word
 * a screen reader user would confuse with page content, and a dense
 * enterprise toolbar reads cleaner icon-only at this density.
 */
export default function AgentWorkerPanel({ enabled, onSelectApplication }) {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState("card");

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const roster = useAgentApplicationsCount(enabled, { pageSize: PAGE_SIZE, page, search });

  const items = roster.items ?? [];
  // Prompt 138W-R5's stale-preserved hook state keeps totalCount/items from
  // the previous successful fetch while a new page/search is loading, so
  // this only reads null before the very first response ever arrives.
  const totalCount = roster.totalCount;
  const totalPages = totalCount ? Math.max(1, Math.ceil(totalCount / PAGE_SIZE)) : 1;
  const rangeStart = totalCount ? (page - 1) * PAGE_SIZE + 1 : 0;
  const rangeEnd = totalCount ? Math.min(page * PAGE_SIZE, totalCount) : 0;
  const isInitialLoad = roster.status === "loading" && items.length === 0;

  return (
    <section className="wx-panel wx-aoc-worker-panel">
      <div className="wx-aoc-worker-head">
        <div>
          <h2 className="wx-panel-title">แรงงานในสายงาน Agent นี้</h2>
          <span className="wx-aoc-worker-total">
            {totalCount === null ? "กำลังโหลด..." : `แรงงาน/ผู้สมัครในสายงานนี้: ${totalCount.toLocaleString("th-TH")} ราย`}
          </span>
        </div>
        <div className="wx-aoc-view-toggle" role="group" aria-label="รูปแบบการแสดงผล">
          <button
            type="button"
            className={viewMode === "card" ? "wx-aoc-view-btn wx-aoc-view-btn--active" : "wx-aoc-view-btn"}
            onClick={() => setViewMode("card")}
            aria-pressed={viewMode === "card"}
            title="มุมมองการ์ด"
            aria-label="มุมมองการ์ด"
          >
            <LayoutGrid size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            className={viewMode === "grid" ? "wx-aoc-view-btn wx-aoc-view-btn--active" : "wx-aoc-view-btn"}
            onClick={() => setViewMode("grid")}
            aria-pressed={viewMode === "grid"}
            title="มุมมองตาราง"
            aria-label="มุมมองตาราง"
          >
            <Rows3 size={16} aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="wx-aoc-worker-toolbar">
        <label className="wx-aoc-search">
          <span className="wx-visually-hidden">ค้นหาแรงงาน</span>
          <Search size={14} aria-hidden="true" className="wx-aoc-search-icon" />
          <input
            type="search"
            placeholder="ค้นหาชื่อแรงงาน เบอร์โทร หรือรหัส..."
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </label>
      </div>

      {isInitialLoad ? (
        <PageSkeleton variant="table" toolbar={false} label="กำลังโหลดรายการแรงงาน..." />
      ) : roster.status === "error" ? (
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <p className="wx-state-body">{describeRosterError(roster.error)}</p>
        </div>
      ) : items.length === 0 ? (
        <p className="wx-empty-list">{search ? "ไม่พบแรงงานที่ตรงกับคำค้นหา" : "ยังไม่มีแรงงานในสายงานนี้"}</p>
      ) : (
        <>
          <p className="wx-aoc-worker-range">
            แสดง {rangeStart}-{rangeEnd} จาก {totalCount.toLocaleString("th-TH")}
          </p>

          {viewMode === "card" ? (
            <div className="wx-aoc-worker-cards">
              {items.map((item) => (
                <ApplicantOrgRow key={item.applicationId} item={item} onClick={() => onSelectApplication(item.applicationId)} />
              ))}
            </div>
          ) : (
            <div className="wx-aoc-worker-grid-wrap">
              <div className="wx-aoc-grid-head" role="row">
                <span role="columnheader">แรงงาน</span>
                <span role="columnheader">สถานะ</span>
                <span role="columnheader">สัญชาติ / ภาษา</span>
                <span role="columnheader">โทรศัพท์</span>
                <span role="columnheader">ส่งเมื่อ</span>
                <span role="columnheader">แหล่งที่มา</span>
                <span role="columnheader" />
              </div>
              <div role="table" aria-label="ตารางแรงงานในสายงาน">
                {items.map((item) => (
                  <ApplicantGridRow key={item.applicationId} item={item} onClick={() => onSelectApplication(item.applicationId)} />
                ))}
              </div>
            </div>
          )}

          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}
    </section>
  );
}
